import { WidgetType } from './widget-registry';
import type { ResolvedEmailBlock } from './block-types';
import type { SocialLink } from './social-widget';
import ButtonWidget from './button-widget';
import ImageWidget from './image-widget';
import LogoWidget from './logo-widget';
import DividerWidget from './divider-widget';
import SpacerWidget from './spacer-widget';
import SocialWidget from './social-widget';
import DateTimeCardWidget, { type DateTimeParts } from './date-time-card-widget';
import type { DateTimeLayout } from './date-time-layout';
import FooterWidget from './footer-widget';
import { WIDGET_COMPONENTS } from './widget-components';
import VideoWidget from './video-widget';
import { BLOCK_ID } from './block-ids';

export interface BlockRendererContext {
  href?: string;
  dateLabel?: string | null;
  timeLabel?: string | null;
  dateParts?: DateTimeParts | null;
  // the template's resolved colors, fonts and radii - the Footer also reads bodyBg (what it sits on),
  // footerText, footerBg and containerRadius
  theme?: Record<string, string | undefined>;
  // src of the Brandlive logo the Footer draws in its strip; unset when the campaign hides it
  brandliveLogo?: string;
  // the Footer's legal-link text in the editor's language
  privacyPolicyLabel?: string;
  // the template's per-slot presets - a block the user added has none of its own, so it borrows
  // from the template's own block of the same kind
  blockPresets?: Record<string, Record<string, unknown>>;
}

// the slots an added block borrows from, in order of preference - the main heading and body copy rather
// than smaller pieces like a pill-shaped salutation or the legal text, and the template's own CTA and footer
const SOURCE_SLOTS: Partial<Record<WidgetType, string[]>> = {
  [WidgetType.Header]: [BLOCK_ID.title, BLOCK_ID.salutation],
  [WidgetType.Paragraph]: [BLOCK_ID.description, BLOCK_ID.tagline],
  [WidgetType.Button]: [BLOCK_ID.cta],
  [WidgetType.Footer]: [BLOCK_ID.mailingAddress],
};

function getSourcePreset(type: WidgetType, presets: BlockRendererContext['blockPresets']) {
  const sourceSlot = SOURCE_SLOTS[type]?.find((id) => presets?.[id]);
  return sourceSlot ? presets?.[sourceSlot] : undefined;
}

// an added Button/Footer already takes the theme's colors, but would otherwise have no font at all - it
// takes its template counterpart's font (a CTA is in the heading font in some templates, the body in others)
function getAddedBlockFont(
  type: WidgetType,
  theme: BlockRendererContext['theme'],
  presets: BlockRendererContext['blockPresets']
) {
  return { fontFamily: (getSourcePreset(type, presets)?.fontFamily as string | undefined) ?? theme?.bodyFont };
}

function getAddedTextBlockStyle(
  type: WidgetType,
  theme: BlockRendererContext['theme'],
  presets: BlockRendererContext['blockPresets']
) {
  const isHeader = type === WidgetType.Header;
  const source = getSourcePreset(type, presets);
  return {
    color: isHeader ? theme?.heading : theme?.body,
    fontFamily: isHeader ? theme?.headingFont : theme?.bodyFont,
    ...(source?.padding !== undefined && { padding: source.padding }),
    ...(source?.margin !== undefined && { margin: source.margin }),
  };
}

export interface BlockRendererProps {
  block: ResolvedEmailBlock;
  // template-authored per-slot style overrides, keyed by the template preset map
  stylePreset?: Record<string, unknown>;
  onValueChange?: (blockId: string, value: string) => void;
  onImageChange?: (blockId: string, src: string) => void;
  context?: BlockRendererContext;
}

// the style panel's generic keys describe the card itself, while the widget's own background/padding (still
// used by template presets) describe the gutter around it - so the user's overrides land on the card props
function toDateTimeCardStyle(style: Record<string, unknown> | undefined): Record<string, unknown> {
  const { background, borderRadius, padding, margin, ...rest } = style ?? {};
  return {
    ...rest,
    ...(background !== undefined && { cardBackground: background }),
    ...(borderRadius !== undefined && { cardBorderRadius: borderRadius }),
    ...(padding !== undefined && { dateRowPadding: padding, timeRowPadding: padding }),
    ...(margin !== undefined && { padding: margin }),
  };
}

export function BlockRenderer({ block, stylePreset, onValueChange, onImageChange, context }: BlockRendererProps) {
  // locked blocks are fixed, view-only content
  const handleValueChange =
    onValueChange && !block.locked ? (value: string) => onValueChange(block.id, value) : undefined;
  const handleImageChange = onImageChange && !block.locked ? (src: string) => onImageChange(block.id, src) : undefined;
  // block.style is the user's own per-instance override, so it wins over the template's default
  const resolvedStyle = { ...stylePreset, ...block.style };
  const addedBlockFont = stylePreset ? undefined : getAddedBlockFont(block.type, context?.theme, context?.blockPresets);

  switch (block.type) {
    case WidgetType.Button:
      return (
        <ButtonWidget
          blockId={block.id}
          value={block.props.value as string | undefined}
          setValue={handleValueChange}
          href={(block.props.href as string | undefined) || context?.href}
          borderRadius={context?.theme?.buttonRadius}
          color={context?.theme?.buttonText}
          buttonColor={context?.theme?.buttonColor}
          {...addedBlockFont}
          {...resolvedStyle}
        />
      );
    case WidgetType.Image:
      return (
        <ImageWidget
          src={block.props.src as string | undefined}
          alt={block.props.alt as string | undefined}
          url={block.props.url as string | undefined}
          setImage={handleImageChange}
          {...resolvedStyle}
        />
      );
    case WidgetType.Video:
      return (
        <VideoWidget
          src={block.props.src as string | undefined}
          alt={block.props.alt as string | undefined}
          url={block.props.url as string | undefined}
          setImage={handleImageChange}
          {...resolvedStyle}
        />
      );
    case WidgetType.Logo:
      return (
        <LogoWidget
          blockId={block.id}
          src={block.props.src as string | undefined}
          setImage={handleImageChange}
          {...resolvedStyle}
        />
      );
    case WidgetType.Divider:
      return <DividerWidget blockId={block.id} {...resolvedStyle} />;
    case WidgetType.Spacer:
      return (
        <SpacerWidget
          blockId={block.id}
          height={(block.props.height as number | string | undefined) ?? 24}
          {...resolvedStyle}
        />
      );
    case WidgetType.Social:
      return (
        <SocialWidget links={block.props.links as SocialLink[] | undefined} context={context} {...resolvedStyle} />
      );
    case WidgetType.DateTimeCard:
      return (
        <DateTimeCardWidget
          blockId={block.id}
          dateLabel={context?.dateLabel}
          timeLabel={context?.timeLabel}
          dateParts={context?.dateParts}
          {...stylePreset}
          {...toDateTimeCardStyle(block.style)}
          // a template preset can set its slot's starting layout; the user's own pick wins over it
          layout={(block.props.layout ?? stylePreset?.layout) as DateTimeLayout | undefined}
        />
      );
    case WidgetType.Footer:
      return (
        <FooterWidget
          blockId={block.id}
          value={block.props.value as string | undefined}
          setValue={handleValueChange}
          src={block.props.src as string | undefined}
          brandliveLogo={context?.brandliveLogo}
          showLegalLink={block.props.showLegalLink as boolean | undefined}
          privacyPolicyUrl={block.props.privacyPolicyUrl as string | undefined}
          privacyPolicyLabel={context?.privacyPolicyLabel}
          bodyColor={context?.theme?.bodyBg}
          borderRadius={context?.theme?.containerRadius}
          accentColor={context?.theme?.accent}
          color={context?.theme?.footerText}
          background={context?.theme?.footerBg}
          {...addedBlockFont}
          {...resolvedStyle}
        />
      );
    case WidgetType.Header:
    case WidgetType.Paragraph: {
      const Component = WIDGET_COMPONENTS[block.type];
      if (!Component) return null;
      // a block the user added has no template preset, so it starts from the template's resolved
      // heading/body color and font, and its main heading/paragraph's spacing, rather than bare defaults
      const addedBlockStyle = stylePreset
        ? undefined
        : getAddedTextBlockStyle(block.type, context?.theme, context?.blockPresets);
      return (
        // keyed so Header's Quill editor (which only reads its initial value at mount) remounts
        // with fresh content when the block underneath it changes, e.g. on a template switch
        <Component
          key={block.id}
          value={block.props.value as string | undefined}
          setValue={handleValueChange}
          {...addedBlockStyle}
          {...resolvedStyle}
          blockId={block.id}
        />
      );
    }
    default:
      return null;
  }
}
